import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Row, Col, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { fires } from '../../firebase';
import firebase from 'firebase';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

const AdminMonthlyAnalytics = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [monthlyData, setMonthlyData] = useState({});
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [dailyData, setDailyData] = useState({});
  const [listData, setListData] = useState({});
  const [eventsList, setEventsList] = useState([]);
  const [eventStatusCounts, setEventStatusCounts] = useState({
    draft: 0,
    deleted: 0,
    published: 0,
    other: 0
  });
  const [sortBy, setSortBy] = useState('orderCount'); // 'orderCount', 'revenue', 'date', 'title'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [viewMode, setViewMode] = useState('list'); // 'monthly', 'daily', or 'list'
  const [selectedListMonth, setSelectedListMonth] = useState(new Date().getMonth() + 1);

  // Sort function
  const sortEventsList = (events, sortBy, sortOrder) => {
    return [...events].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'orderCount':
          aValue = a.orderCount;
          bValue = b.orderCount;
          break;
        case 'revenue':
          aValue = a.totalRevenue;
          bValue = b.totalRevenue;
          break;
        case 'date':
          aValue = a.startDate.toDate();
          bValue = b.startDate.toDate();
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        default:
          aValue = a.orderCount;
          bValue = b.orderCount;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Sorted events list
  const sortedEventsList = useMemo(() => {
    return sortEventsList(eventsList, sortBy, sortOrder);
  }, [eventsList, sortBy, sortOrder]);

  // Check if user has management role
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!currentUser) {
        navigate('/');
        setIsCheckingAccess(false);
        return;
      }

      try {
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setIsCheckingAccess(false);
          navigate('/');
          return;
        }
        
        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setIsCheckingAccess(false);
          navigate('/');
          return;
        }
        
        setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin access:', error);
        setIsAdmin(false);
        navigate('/');
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAdminAccess();
  }, [currentUser, navigate]);

  // Load monthly analytics data
  useEffect(() => {
    if (isAdmin) {
      if (viewMode === 'monthly') {
        loadMonthlyAnalytics();
      } else if (viewMode === 'daily') {
        loadDailyAnalytics();
      } else if (viewMode === 'list') {
        loadListAnalytics();
        loadEventsList();
      }
    }
  }, [isAdmin, currentYear, selectedMonth, selectedListMonth, viewMode]);

  const loadMonthlyAnalytics = async () => {
    setLoading(true);
    setError('');
    
    try {
      const monthlyStats = {};
      
      // Initialize all months for current year with empty data
      for (let month = 1; month <= 12; month++) {
        const monthKey = `${currentYear}-${month.toString().padStart(2, '0')}`;
        monthlyStats[monthKey] = {
          month: month,
          monthName: moment().month(month - 1).format('MMMM'),
          pageViews: 0,
          registeredUsers: 0,
          participantUsers: 0,
          totalEvents: 0,
          freeEvents: 0,
          paidEvents: 0,
          totalTicketSales: 0,
          freeTickets: 0,
          paidTickets: 0,
          totalRevenue: 0,
          loaded: false
        };
      }

      // Only load current month initially
      const currentMonth = new Date().getMonth() + 1;
      await loadSingleMonthData(monthlyStats, currentMonth);

      setMonthlyData(monthlyStats);
    } catch (err) {
      setError('Failed to load analytics data: ' + err.message);
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSingleMonthData = async (monthlyStats, monthNumber) => {
    const monthKey = `${currentYear}-${monthNumber.toString().padStart(2, '0')}`;
    if (monthlyStats[monthKey] && monthlyStats[monthKey].loaded) {
      return; // Already loaded
    }

    const startOfMonth = moment().year(currentYear).month(monthNumber - 1).startOf('month').toDate();
    const endOfMonth = moment().year(currentYear).month(monthNumber - 1).endOf('month').toDate();

    // Load page views for this month
    await loadPageViewsForMonth(monthlyStats, monthKey, startOfMonth, endOfMonth);
    
    // Load registered users for this month (from users collection)
    await loadRegisteredUsersFromUsersCollection(monthlyStats, monthKey, startOfMonth, endOfMonth);
    
    // Load participant users for this month (from orders, counting repeats)
    await loadParticipantUsersForMonth(monthlyStats, monthKey, startOfMonth, endOfMonth);
    
    // Load events for this month
    await loadEventsForMonth(monthlyStats, monthKey, startOfMonth, endOfMonth);
    
    // Load ticket sales for this month
    await loadTicketSalesForMonth(monthlyStats, monthKey, startOfMonth, endOfMonth);

    if (monthlyStats[monthKey]) {
      monthlyStats[monthKey].loaded = true;
    }
  };

  const loadListAnalytics = async () => {
    setLoading(true);
    setError('');
    
    try {
      const listStats = {
        pageViews: 0,
        registeredUsers: 0,
        participantUsers: 0,
        totalEvents: 0,
        freeEvents: 0,
        paidEvents: 0,
        totalTicketSales: 0,
        freeTickets: 0,
        paidTickets: 0,
        totalRevenue: 0
      };

      const startOfMonth = moment().year(currentYear).month(selectedListMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedListMonth - 1).endOf('month').toDate();

      // Load page views
      await loadPageViewsForList(listStats, startOfMonth, endOfMonth);
      
      // Load registered users (from users collection)
      await loadRegisteredUsersFromUsersCollectionForList(listStats, startOfMonth, endOfMonth);
      
      // Load participant users (from orders, counting repeats)
      await loadParticipantUsersForList(listStats, startOfMonth, endOfMonth);
      
      // Load events
      await loadEventsForList(listStats, startOfMonth, endOfMonth);
      
      // Load ticket sales and orders
      await loadTicketSalesForList(listStats, startOfMonth, endOfMonth);

      setListData(listStats);
    } catch (err) {
      setError('Failed to load list analytics data: ' + err.message);
      console.error('List analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyAnalytics = async () => {
    setLoading(true);
    setError('');
    
    try {
      const dailyStats = {};
      const daysInMonth = moment().year(currentYear).month(selectedMonth - 1).daysInMonth();
      
      // Initialize all days for selected month
      for (let day = 1; day <= daysInMonth; day++) {
        const dayKey = `${currentYear}-${selectedMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        dailyStats[dayKey] = {
          day: day,
          pageViews: 0,
          registeredUsers: 0,
          participantUsers: 0,
          totalEvents: 0,
          freeEvents: 0,
          paidEvents: 0,
          totalTicketSales: 0,
          freeTickets: 0,
          paidTickets: 0,
          totalRevenue: 0
        };
      }

      // Load page views
      await loadDailyPageViews(dailyStats);
      
      // Load registered users (from users collection)
      await loadDailyRegisteredUsersFromUsersCollection(dailyStats);
      
      // Load participant users (from orders, counting repeats)
      await loadDailyParticipantUsers(dailyStats);
      
      // Load events
      await loadDailyEvents(dailyStats);
      
      // Load ticket sales and orders
      await loadDailyTicketSales(dailyStats);

      setDailyData(dailyStats);
    } catch (err) {
      setError('Failed to load daily analytics data: ' + err.message);
      console.error('Daily analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPageViewsForMonth = async (monthlyStats, monthKey, startOfMonth, endOfMonth) => {
    try {
      const pageViewsSnapshot = await fires
        .collection('page_views')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      pageViewsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const viewDate = data.timestamp.toDate();
        const docMonthKey = moment(viewDate).format('YYYY-MM');
        
        if (monthlyStats[docMonthKey]) {
          monthlyStats[docMonthKey].pageViews++;
        }
      });
    } catch (error) {
      console.error('Error loading page views for month:', error);
    }
  };

  const loadRegisteredUsersForMonth = async (monthlyStats, monthKey, startOfMonth, endOfMonth) => {
    try {
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Track unique emails for this month
      const uniqueEmails = new Set();
      
      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const orderDate = data.timestamp.toDate();
        const docMonthKey = moment(orderDate).format('YYYY-MM');
        const userEmail = data.userEmail;
        
        if (monthlyStats[docMonthKey] && userEmail) {
          uniqueEmails.add(userEmail);
        }
      });

      // Update monthly stats with unique email count
      if (monthlyStats[monthKey]) {
        monthlyStats[monthKey].registeredUsers = uniqueEmails.size;
      }
    } catch (error) {
      console.error('Error loading registered users for month:', error);
    }
  };

  const loadRegisteredUsersFromUsersCollection = async (monthlyStats, monthKey, startOfMonth, endOfMonth) => {
    try {
      const usersSnapshot = await fires
        .collection('users')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Update monthly stats with user count from users collection
      if (monthlyStats[monthKey]) {
        monthlyStats[monthKey].registeredUsers = usersSnapshot.docs.length;
      }
    } catch (error) {
      console.error('Error loading registered users from users collection:', error);
    }
  };

  const loadParticipantUsersForMonth = async (monthlyStats, monthKey, startOfMonth, endOfMonth) => {
    try {
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Count all orders (including repeats) for participant users
      let participantCount = 0;
      
      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const orderDate = data.timestamp.toDate();
        const docMonthKey = moment(orderDate).format('YYYY-MM');
        const userEmail = data.userEmail;
        
        if (monthlyStats[docMonthKey] && userEmail) {
          participantCount++;
        }
      });

      // Update monthly stats with participant count (including repeats)
      if (monthlyStats[monthKey]) {
        monthlyStats[monthKey].participantUsers = participantCount;
      }
    } catch (error) {
      console.error('Error loading participant users for month:', error);
    }
  };

  const loadEventsForMonth = async (monthlyStats, monthKey, startOfMonth, endOfMonth) => {
    try {
      const eventsSnapshot = await fires
        .collection('events')
        .where('startDate', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('startDate', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      eventsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const eventDate = data.startDate.toDate();
        const docMonthKey = moment(eventDate).format('YYYY-MM');
        
        if (monthlyStats[docMonthKey]) {
          monthlyStats[docMonthKey].totalEvents++;
          
          // Free/paid will be determined by revenue in loadTicketSalesForMonth
          // For now, just count total events
        }
      });
    } catch (error) {
      console.error('Error loading events for month:', error);
    }
  };

  const loadTicketSalesForMonth = async (monthlyStats, monthKey, startOfMonth, endOfMonth) => {
    try {
      // Load tickets from event_tickets collection
      const ticketsSnapshot = await fires
        .collection('event_tickets')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      ticketsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const ticketDate = data.timestamp.toDate();
        const docMonthKey = moment(ticketDate).format('YYYY-MM');
        
        if (monthlyStats[docMonthKey]) {
          monthlyStats[docMonthKey].totalTicketSales++;
          
          // Check if ticket is free or paid based on paymentId
          const paymentId = data.paymentId;
          if (!paymentId || paymentId === '') {
            monthlyStats[docMonthKey].freeTickets++;
          } else {
            monthlyStats[docMonthKey].paidTickets++;
          }
        }
      });

      // Load orders for revenue data
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const orderDate = data.timestamp.toDate();
        const docMonthKey = moment(orderDate).format('YYYY-MM');
        
        if (monthlyStats[docMonthKey]) {
          const amount = data.amount || 0;
          monthlyStats[docMonthKey].totalRevenue += amount;
        }
      });

      // Now count free/paid events based on prices map
      // First, get all events for this month
      const eventsSnapshot = await fires
        .collection('events')
        .where('startDate', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('startDate', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      eventsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const eventDate = data.startDate.toDate();
        const docMonthKey = moment(eventDate).format('YYYY-MM');
        
        if (monthlyStats[docMonthKey]) {
          // Reset free/paid counts first
          monthlyStats[docMonthKey].freeEvents = 0;
          monthlyStats[docMonthKey].paidEvents = 0;
        }
      });

      // Recalculate free/paid events based on prices map
      eventsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const eventDate = data.startDate.toDate();
        const docMonthKey = moment(eventDate).format('YYYY-MM');
        
        if (monthlyStats[docMonthKey]) {
          // Check if event is free or paid based on prices map
          const prices = data.prices || {};
          const priceObjects = Object.values(prices);
          const priceValues = priceObjects.map(p => p.price || 0);
          const isFree = priceValues.length === 0 || priceValues.every(price => price === 0 || price === '0');
          
          if (isFree) {
            monthlyStats[docMonthKey].freeEvents++;
          } else {
            monthlyStats[docMonthKey].paidEvents++;
          }
        }
      });
    } catch (error) {
      console.error('Error loading ticket sales for month:', error);
    }
  };

  // List data loading functions
  const loadPageViewsForList = async (listStats, startOfMonth, endOfMonth) => {
    try {
      const pageViewsSnapshot = await fires
        .collection('page_views')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      listStats.pageViews = pageViewsSnapshot.docs.length;
    } catch (error) {
      console.error('Error loading page views for list:', error);
    }
  };

  const loadRegisteredUsersForList = async (listStats, startOfMonth, endOfMonth) => {
    try {
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      const uniqueEmails = new Set();
      
      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const userEmail = data.userEmail;
        if (userEmail) {
          uniqueEmails.add(userEmail);
        }
      });

      listStats.registeredUsers = uniqueEmails.size;
    } catch (error) {
      console.error('Error loading registered users for list:', error);
    }
  };

  const loadRegisteredUsersFromUsersCollectionForList = async (listStats, startOfMonth, endOfMonth) => {
    try {
      const usersSnapshot = await fires
        .collection('users')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      listStats.registeredUsers = usersSnapshot.docs.length;
    } catch (error) {
      console.error('Error loading registered users from users collection for list:', error);
    }
  };

  const loadParticipantUsersForList = async (listStats, startOfMonth, endOfMonth) => {
    try {
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Count all orders (including repeats) for participant users
      let participantCount = 0;
      
      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const userEmail = data.userEmail;
        if (userEmail) {
          participantCount++;
        }
      });

      listStats.participantUsers = participantCount;
    } catch (error) {
      console.error('Error loading participant users for list:', error);
    }
  };

  const loadEventsForList = async (listStats, startOfMonth, endOfMonth) => {
    try {
      const eventsSnapshot = await fires
        .collection('events')
        .where('startDate', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('startDate', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      eventsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        listStats.totalEvents++;
        
        // Free/paid will be determined by revenue in loadTicketSalesForList
        // For now, just count total events
      });
    } catch (error) {
      console.error('Error loading events for list:', error);
    }
  };

  const loadTicketSalesForList = async (listStats, startOfMonth, endOfMonth) => {
    try {
      // Load tickets from event_tickets collection
      const ticketsSnapshot = await fires
        .collection('event_tickets')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      ticketsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        listStats.totalTicketSales++;
        
        // Check if ticket is free or paid based on paymentId
        const paymentId = data.paymentId;
        if (!paymentId || paymentId === '') {
          listStats.freeTickets++;
        } else {
          listStats.paidTickets++;
        }
      });

      // Load orders for revenue data
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const amount = data.amount || 0;
        listStats.totalRevenue += amount;
      });

      // Now count free/paid events based on prices map
      // First, get all events for this month
      const eventsSnapshot = await fires
        .collection('events')
        .where('startDate', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('startDate', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Reset free/paid counts
      listStats.freeEvents = 0;
      listStats.paidEvents = 0;

      // Recalculate free/paid events based on prices map
      eventsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Check if event is free or paid based on prices map
        const prices = data.prices || {};
        const priceObjects = Object.values(prices);
        const priceValues = priceObjects.map(p => p.price || 0);
        const isFree = priceValues.length === 0 || priceValues.every(price => price === 0 || price === '0');
        
        if (isFree) {
          listStats.freeEvents++;
        } else {
          listStats.paidEvents++;
        }
      });
    } catch (error) {
      console.error('Error loading ticket sales for list:', error);
    }
  };

  // Daily data loading functions
  const loadDailyPageViews = async (dailyStats) => {
    try {
      const startOfMonth = moment().year(currentYear).month(selectedMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedMonth - 1).endOf('month').toDate();
      
      const pageViewsSnapshot = await fires
        .collection('page_views')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      pageViewsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const viewDate = data.timestamp.toDate();
        const dayKey = moment(viewDate).format('YYYY-MM-DD');
        
        if (dailyStats[dayKey]) {
          dailyStats[dayKey].pageViews++;
        }
      });
    } catch (error) {
      console.error('Error loading daily page views:', error);
    }
  };

  const loadDailyRegisteredUsers = async (dailyStats) => {
    try {
      const startOfMonth = moment().year(currentYear).month(selectedMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedMonth - 1).endOf('month').toDate();
      
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Track unique emails per day
      const dailyUniqueEmails = {};
      
      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const orderDate = data.timestamp.toDate();
        const dayKey = moment(orderDate).format('YYYY-MM-DD');
        const userEmail = data.userEmail;
        
        if (dailyStats[dayKey] && userEmail) {
          if (!dailyUniqueEmails[dayKey]) {
            dailyUniqueEmails[dayKey] = new Set();
          }
          dailyUniqueEmails[dayKey].add(userEmail);
        }
      });

      // Update daily stats with unique email counts
      Object.keys(dailyUniqueEmails).forEach(dayKey => {
        if (dailyStats[dayKey]) {
          dailyStats[dayKey].registeredUsers = dailyUniqueEmails[dayKey].size;
        }
      });
    } catch (error) {
      console.error('Error loading daily registered users:', error);
    }
  };

  const loadDailyRegisteredUsersFromUsersCollection = async (dailyStats) => {
    try {
      const startOfMonth = moment().year(currentYear).month(selectedMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedMonth - 1).endOf('month').toDate();
      
      const usersSnapshot = await fires
        .collection('users')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Track users per day
      const dailyUserCounts = {};
      
      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const userDate = data.timestamp.toDate();
        const dayKey = moment(userDate).format('YYYY-MM-DD');
        
        if (dailyStats[dayKey]) {
          if (!dailyUserCounts[dayKey]) {
            dailyUserCounts[dayKey] = 0;
          }
          dailyUserCounts[dayKey]++;
        }
      });

      // Update daily stats with user counts
      Object.keys(dailyUserCounts).forEach(dayKey => {
        if (dailyStats[dayKey]) {
          dailyStats[dayKey].registeredUsers = dailyUserCounts[dayKey];
        }
      });
    } catch (error) {
      console.error('Error loading daily registered users from users collection:', error);
    }
  };

  const loadDailyParticipantUsers = async (dailyStats) => {
    try {
      const startOfMonth = moment().year(currentYear).month(selectedMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedMonth - 1).endOf('month').toDate();
      
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Track participant counts per day (including repeats)
      const dailyParticipantCounts = {};
      
      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const orderDate = data.timestamp.toDate();
        const dayKey = moment(orderDate).format('YYYY-MM-DD');
        const userEmail = data.userEmail;
        
        if (dailyStats[dayKey] && userEmail) {
          if (!dailyParticipantCounts[dayKey]) {
            dailyParticipantCounts[dayKey] = 0;
          }
          dailyParticipantCounts[dayKey]++;
        }
      });

      // Update daily stats with participant counts
      Object.keys(dailyParticipantCounts).forEach(dayKey => {
        if (dailyStats[dayKey]) {
          dailyStats[dayKey].participantUsers = dailyParticipantCounts[dayKey];
        }
      });
    } catch (error) {
      console.error('Error loading daily participant users:', error);
    }
  };

  const loadEventsList = async () => {
    try {
      const startOfMonth = moment().year(currentYear).month(selectedListMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedListMonth - 1).endOf('month').toDate();
      
      // Load events for the selected month
      const eventsSnapshot = await fires
        .collection('events')
        .where('startDate', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('startDate', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      const eventsWithOrders = [];
      const statusCounts = {
        draft: 0,
        deleted: 0,
        published: 0,
        other: 0
      };

      // For each event, count the orders
      for (const eventDoc of eventsSnapshot.docs) {
        const eventData = eventDoc.data();
        const eventId = eventDoc.id;
        
        // Determine event status
        let status = 'other';
        if (eventData.status === 'draft') {
          status = 'draft';
        } else if (eventData.status === 'deleted') {
          status = 'deleted';
        } else if (eventData.status === 'published' || eventData.status === 'active') {
          status = 'published';
        } else if (eventData.status) {
          status = 'other';
        }
        
        statusCounts[status]++;
        
        // Count orders for this event
        const ordersSnapshot = await fires
          .collection('orders')
          .where('eventId', '==', eventId)
          .get();

        const orderCount = ordersSnapshot.docs.length;
        const totalRevenue = ordersSnapshot.docs.reduce((sum, orderDoc) => {
          const orderData = orderDoc.data();
          return sum + (orderData.amount || 0);
        }, 0);

        // Determine if event is free or paid based on prices map
        const prices = eventData.prices || {};
        const priceObjects = Object.values(prices);
        const priceValues = priceObjects.map(p => p.price || 0);
        const isFree = priceValues.length === 0 || priceValues.every(price => price === 0 || price === '0');
        const maxPrice = priceValues.length > 0 ? Math.max(...priceValues.map(p => parseFloat(p) || 0)) : 0;

        eventsWithOrders.push({
          id: eventId,
          title: eventData.title || 'Untitled Event',
          startDate: eventData.startDate,
          endDate: eventData.endDate,
          prices: prices,
          isFree: isFree,
          maxPrice: maxPrice,
          orderCount: orderCount,
          totalRevenue: totalRevenue,
          location: eventData.location || 'TBD',
          organizer: eventData.organizer || 'Unknown',
          status: status
        });
      }

      // Sort by start date
      eventsWithOrders.sort((a, b) => {
        const dateA = a.startDate.toDate();
        const dateB = b.startDate.toDate();
        return dateA - dateB;
      });

      setEventsList(eventsWithOrders);
      setEventStatusCounts(statusCounts);
    } catch (error) {
      console.error('Error loading events list:', error);
      setEventsList([]);
    }
  };

  const loadDailyEvents = async (dailyStats) => {
    try {
      const startOfMonth = moment().year(currentYear).month(selectedMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedMonth - 1).endOf('month').toDate();
      
      const eventsSnapshot = await fires
        .collection('events')
        .where('startDate', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('startDate', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      eventsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const eventDate = data.startDate.toDate();
        const dayKey = moment(eventDate).format('YYYY-MM-DD');
        
        if (dailyStats[dayKey]) {
          dailyStats[dayKey].totalEvents++;
          
          // Free/paid will be determined by revenue in loadDailyTicketSales
          // For now, just count total events
        }
      });
    } catch (error) {
      console.error('Error loading daily events:', error);
    }
  };

  const loadDailyTicketSales = async (dailyStats) => {
    try {
      const startOfMonth = moment().year(currentYear).month(selectedMonth - 1).startOf('month').toDate();
      const endOfMonth = moment().year(currentYear).month(selectedMonth - 1).endOf('month').toDate();
      
      // Load tickets from event_tickets collection
      const ticketsSnapshot = await fires
        .collection('event_tickets')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      ticketsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const ticketDate = data.timestamp.toDate();
        const dayKey = moment(ticketDate).format('YYYY-MM-DD');
        
        if (dailyStats[dayKey]) {
          dailyStats[dayKey].totalTicketSales++;
          
          // Check if ticket is free or paid based on paymentId
          const paymentId = data.paymentId;
          if (!paymentId || paymentId === '') {
            dailyStats[dayKey].freeTickets++;
          } else {
            dailyStats[dayKey].paidTickets++;
          }
        }
      });

      // Load orders for revenue data
      const ordersSnapshot = await fires
        .collection('orders')
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      ordersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const orderDate = data.timestamp.toDate();
        const dayKey = moment(orderDate).format('YYYY-MM-DD');
        
        if (dailyStats[dayKey]) {
          const amount = data.amount || 0;
          dailyStats[dayKey].totalRevenue += amount;
        }
      });

      // Now count free/paid events based on prices map
      // First, get all events for this month
      const eventsSnapshot = await fires
        .collection('events')
        .where('startDate', '>=', firebase.firestore.Timestamp.fromDate(startOfMonth))
        .where('startDate', '<=', firebase.firestore.Timestamp.fromDate(endOfMonth))
        .get();

      // Reset free/paid counts for all days
      Object.keys(dailyStats).forEach(dayKey => {
        if (dailyStats[dayKey]) {
          dailyStats[dayKey].freeEvents = 0;
          dailyStats[dayKey].paidEvents = 0;
        }
      });

      // Recalculate free/paid events based on prices map
      eventsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const eventDate = data.startDate.toDate();
        const dayKey = moment(eventDate).format('YYYY-MM-DD');
        
        if (dailyStats[dayKey]) {
          // Check if event is free or paid based on prices map
          const prices = data.prices || {};
          const priceObjects = Object.values(prices);
          const priceValues = priceObjects.map(p => p.price || 0);
          const isFree = priceValues.length === 0 || priceValues.every(price => price === 0 || price === '0');
          
          if (isFree) {
            dailyStats[dayKey].freeEvents++;
          } else {
            dailyStats[dayKey].paidEvents++;
          }
        }
      });
    } catch (error) {
      console.error('Error loading daily ticket sales:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const getTotalForYear = (field) => {
    return Object.values(monthlyData).reduce((total, month) => total + month[field], 0);
  };

  const getTotalForMonth = (field) => {
    return Object.values(dailyData).reduce((total, day) => total + day[field], 0);
  };

  const hasData = viewMode === 'monthly' 
    ? Object.values(monthlyData).some(month => 
        month.pageViews > 0 || month.registeredUsers > 0 || month.participantUsers > 0 || month.totalEvents > 0 || month.totalTicketSales > 0
      )
    : viewMode === 'daily'
    ? Object.values(dailyData).some(day => 
        day.pageViews > 0 || day.registeredUsers > 0 || day.participantUsers > 0 || day.totalEvents > 0 || day.totalTicketSales > 0
      )
    : listData.pageViews > 0 || listData.registeredUsers > 0 || listData.participantUsers > 0 || listData.totalEvents > 0 || listData.totalTicketSales > 0;

  const getMonthName = (monthNumber) => {
    return moment().month(monthNumber - 1).format('MMMM');
  };

  const handleMonthClick = async (monthNumber) => {
    if (viewMode === 'monthly') {
      const monthKey = `${currentYear}-${monthNumber.toString().padStart(2, '0')}`;
      if (monthlyData[monthKey] && !monthlyData[monthKey].loaded) {
        setLoading(true);
        try {
          await loadSingleMonthData(monthlyData, monthNumber);
          setMonthlyData({...monthlyData});
        } catch (error) {
          console.error('Error loading month data:', error);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  if (isCheckingAccess) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Checking access...</p>
        </div>
      </Container>
    );
  }

  if (!currentUser || !isAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You don't have permission to access this page. Please contact an administrator.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Header>
            <h4 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>
              Monthly Analytics Dashboard - {currentYear}
            </h4>
          </Card.Header>
          <Card.Body>
            <div className="text-center py-5">
              <Spinner animation="border" role="status" size="lg">
                <span className="visually-hidden">Loading analytics data...</span>
              </Spinner>
              <p className="mt-3 mb-0">Loading {viewMode} analytics for {viewMode === 'monthly' ? currentYear : viewMode === 'daily' ? `${getMonthName(selectedMonth)} ${currentYear}` : `${getMonthName(selectedListMonth)} ${currentYear}`}...</p>
              <small className="text-muted">This may take a few moments to gather all data</small>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!hasData) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Header>
            <h4 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>
              Monthly Analytics Dashboard - {currentYear}
            </h4>
          </Card.Header>
          <Card.Body>
            <div className="text-center py-5">
              <i className="fas fa-chart-bar fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No Data Available</h5>
              <p className="text-muted">No analytics data found for {viewMode === 'monthly' ? currentYear : viewMode === 'daily' ? `${getMonthName(selectedMonth)} ${currentYear}` : `${getMonthName(selectedListMonth)} ${currentYear}`}. Data will appear here once users start interacting with the platform.</p>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fas fa-chart-line me-2"></i>
                  {viewMode === 'monthly' ? 'Monthly' : viewMode === 'daily' ? 'Daily' : 'List'} Analytics Dashboard - {viewMode === 'monthly' ? currentYear : viewMode === 'daily' ? `${getMonthName(selectedMonth)} ${currentYear}` : `${getMonthName(selectedListMonth)} ${currentYear}`}
                </h4>
                <div className="d-flex gap-2">
                  <select 
                    className="form-select form-select-sm" 
                    value={viewMode} 
                    onChange={(e) => setViewMode(e.target.value)}
                    style={{width: 'auto'}}
                  >
                    <option value="list">List View</option>
                    <option value="monthly">Monthly View</option>
                    <option value="daily">Daily View</option>
                  </select>
                  {viewMode === 'list' && (
                    <div className="d-flex gap-2 align-items-center">
                      <label className="form-label mb-0">Year:</label>
                      <select 
                        className="form-select form-select-sm" 
                        value={currentYear} 
                        onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                        style={{width: '100px'}}
                      >
                        {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      <label className="form-label mb-0">Month:</label>
                      <select 
                        className="form-select form-select-sm" 
                        value={selectedListMonth} 
                        onChange={(e) => setSelectedListMonth(parseInt(e.target.value))}
                        style={{width: '150px'}}
                      >
                        {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>{getMonthName(month)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {viewMode === 'monthly' && (
                    <div className="d-flex gap-2 align-items-center">
                      <label className="form-label mb-0">Year:</label>
                      <select 
                        className="form-select form-select-sm" 
                        value={currentYear} 
                        onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                        style={{width: '100px'}}
                      >
                        {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {viewMode === 'daily' && (
                    <div className="d-flex gap-2 align-items-center">
                      <label className="form-label mb-0">Year:</label>
                      <select 
                        className="form-select form-select-sm" 
                        value={currentYear} 
                        onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                        style={{width: '100px'}}
                      >
                        {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      <label className="form-label mb-0">Month:</label>
                      <select 
                        className="form-select form-select-sm" 
                        value={selectedMonth} 
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        style={{width: '150px'}}
                      >
                        {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>{getMonthName(month)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Summary Cards */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-primary">{(viewMode === 'monthly' ? getTotalForYear('pageViews') : viewMode === 'daily' ? getTotalForMonth('pageViews') : listData.pageViews).toLocaleString()}</h5>
                      <p className="mb-0">Total Page Views</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-success">{(viewMode === 'monthly' ? getTotalForYear('registeredUsers') : viewMode === 'daily' ? getTotalForMonth('registeredUsers') : listData.registeredUsers).toLocaleString()}</h5>
                      <p className="mb-0">Registered Users</p>
                      <small className="text-muted">(from users collection)</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-warning">{(viewMode === 'monthly' ? getTotalForYear('participantUsers') : viewMode === 'daily' ? getTotalForMonth('participantUsers') : listData.participantUsers).toLocaleString()}</h5>
                      <p className="mb-0">Participant Users</p>
                      <small className="text-muted">(from orders, includes repeats)</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-info">{(viewMode === 'monthly' ? getTotalForYear('totalEvents') : viewMode === 'daily' ? getTotalForMonth('totalEvents') : listData.totalEvents).toLocaleString()}</h5>
                      <p className="mb-0">Total Events</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-warning">{formatCurrency(viewMode === 'monthly' ? getTotalForYear('totalRevenue') : viewMode === 'daily' ? getTotalForMonth('totalRevenue') : listData.totalRevenue)}</h5>
                      <p className="mb-0">Total Revenue</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Data Display */}
              {viewMode === 'list' ? (
                <div>
                  <h5 className="mb-4">Analytics Summary for {getMonthName(selectedListMonth)} {currentYear}</h5>
                  <Row>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-eye fa-2x text-primary mb-2"></i>
                          <h4 className="text-primary">{listData.pageViews.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Page Views</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-users fa-2x text-success mb-2"></i>
                          <h4 className="text-success">{listData.registeredUsers.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Registered Users</p>
                          <small className="text-muted">(from users collection)</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-user-check fa-2x text-warning mb-2"></i>
                          <h4 className="text-warning">{listData.participantUsers.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Participant Users</p>
                          <small className="text-muted">(from orders, includes repeats)</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-calendar-alt fa-2x text-info mb-2"></i>
                          <h4 className="text-info">{listData.totalEvents.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Total Events</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-ticket-alt fa-2x text-dark mb-2"></i>
                          <h4 className="text-dark">{listData.totalTicketSales.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Total Tickets</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-gift fa-2x text-secondary mb-2"></i>
                          <h4 className="text-secondary">{listData.freeEvents.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Free Events</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-dollar-sign fa-2x text-warning mb-2"></i>
                          <h4 className="text-warning">{listData.paidEvents.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Paid Events</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-ticket-alt fa-2x text-light mb-2" style={{color: '#6c757d !important'}}></i>
                          <h4 className="text-muted">{listData.freeTickets.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Free Tickets</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-ticket-alt fa-2x text-danger mb-2"></i>
                          <h4 className="text-danger">{listData.paidTickets.toLocaleString()}</h4>
                          <p className="mb-0 text-muted">Paid Tickets</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12} className="mb-3">
                      <Card className="text-center h-100">
                        <Card.Body>
                          <i className="fas fa-chart-line fa-2x text-warning mb-2"></i>
                          <h4 className="text-warning">{formatCurrency(listData.totalRevenue)}</h4>
                          <p className="mb-0 text-muted">Total Revenue</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">{viewMode === 'monthly' ? 'Monthly Breakdown' : 'Daily Breakdown'}</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table striped hover>
                        <thead>
                          <tr>
                            <th>{viewMode === 'monthly' ? 'Month' : 'Day'}</th>
                            <th>Page Views</th>
                            <th>Registered Users</th>
                            <th>Participant Users</th>
                            <th>Total Events</th>
                            <th>Free Events</th>
                            <th>Paid Events</th>
                            <th>Total Tickets</th>
                            <th>Free Tickets</th>
                            <th>Paid Tickets</th>
                            <th>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                        {viewMode === 'monthly' 
                          ? Object.values(monthlyData).map((month) => (
                              <tr 
                                key={month.month}
                                style={{ 
                                  cursor: !month.loaded ? 'pointer' : 'default',
                                  opacity: !month.loaded ? 0.6 : 1
                                }}
                                onClick={() => handleMonthClick(month.month)}
                                title={!month.loaded ? 'Click to load data for this month' : ''}
                              >
                                <td>
                                  <strong>
                                    {month.monthName}
                                    {!month.loaded && <span className="text-muted ms-2">(Click to load)</span>}
                                  </strong>
                                </td>
                                <td>
                                  <Badge bg="primary">{month.pageViews.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="success">{month.registeredUsers.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="warning">{month.participantUsers.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="info">{month.totalEvents.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="secondary">{month.freeEvents.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="warning">{month.paidEvents.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="dark">{month.totalTicketSales.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="light" text="dark">{month.freeTickets.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="danger">{month.paidTickets.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <strong className="text-success">{formatCurrency(month.totalRevenue)}</strong>
                                </td>
                              </tr>
                            ))
                          : Object.values(dailyData).map((day) => (
                              <tr key={day.day}>
                                <td>
                                  <strong>Day {day.day}</strong>
                                </td>
                                <td>
                                  <Badge bg="primary">{day.pageViews.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="success">{day.registeredUsers.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="warning">{day.participantUsers.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="info">{day.totalEvents.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="secondary">{day.freeEvents.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="warning">{day.paidEvents.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="dark">{day.totalTicketSales.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="light" text="dark">{day.freeTickets.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <Badge bg="danger">{day.paidTickets.toLocaleString()}</Badge>
                                </td>
                                <td>
                                  <strong className="text-success">{formatCurrency(day.totalRevenue)}</strong>
                                </td>
                              </tr>
                            ))
                        }
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Users Summary */}
              <Row className="mt-4">
                <Col md={12}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Users Summary</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Registered Users:</span>
                            <Badge bg="success">{(viewMode === 'monthly' ? getTotalForYear('registeredUsers') : viewMode === 'daily' ? getTotalForMonth('registeredUsers') : listData.registeredUsers).toLocaleString()}</Badge>
                          </div>
                          <small className="text-muted">(from users collection)</small>
                        </Col>
                        <Col md={3}>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Participant Users:</span>
                            <Badge bg="warning">{(viewMode === 'monthly' ? getTotalForYear('participantUsers') : viewMode === 'daily' ? getTotalForMonth('participantUsers') : listData.participantUsers).toLocaleString()}</Badge>
                          </div>
                          <small className="text-muted">(from orders, includes repeats)</small>
                        </Col>
                        <Col md={3}>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Participation Rate:</span>
                            <span>
                              {(viewMode === 'monthly' ? getTotalForYear('registeredUsers') : viewMode === 'daily' ? getTotalForMonth('registeredUsers') : listData.registeredUsers) > 0 
                                ? `${(((viewMode === 'monthly' ? getTotalForYear('participantUsers') : viewMode === 'daily' ? getTotalForMonth('participantUsers') : listData.participantUsers) / (viewMode === 'monthly' ? getTotalForYear('registeredUsers') : viewMode === 'daily' ? getTotalForMonth('registeredUsers') : listData.registeredUsers)) * 100).toFixed(1)}%`
                                : 'N/A'
                              }
                            </span>
                          </div>
                          <small className="text-muted">(participants per registered user)</small>
                        </Col>
                        <Col md={3}>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Avg Orders per User:</span>
                            <span>
                              {(viewMode === 'monthly' ? getTotalForYear('registeredUsers') : viewMode === 'daily' ? getTotalForMonth('registeredUsers') : listData.registeredUsers) > 0 
                                ? ((viewMode === 'monthly' ? getTotalForYear('participantUsers') : viewMode === 'daily' ? getTotalForMonth('participantUsers') : listData.participantUsers) / (viewMode === 'monthly' ? getTotalForYear('registeredUsers') : viewMode === 'daily' ? getTotalForMonth('registeredUsers') : listData.registeredUsers)).toFixed(1)
                                : 'N/A'
                              }
                            </span>
                          </div>
                          <small className="text-muted">(orders per registered user)</small>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Events List */}
              {viewMode === 'list' && (
                <Row className="mt-4">
                  <Col md={12}>
                    <Card>
                      <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">Events List - {getMonthName(selectedListMonth)} {currentYear}</h6>
                          <div className="d-flex gap-2">
                            <select 
                              className="form-select form-select-sm" 
                              value={sortBy} 
                              onChange={(e) => setSortBy(e.target.value)}
                              style={{ width: 'auto' }}
                            >
                              <option value="orderCount">Most Orders</option>
                              <option value="revenue">Most Revenue</option>
                              <option value="date">Date</option>
                              <option value="title">Title</option>
                            </select>
                            <select 
                              className="form-select form-select-sm" 
                              value={sortOrder} 
                              onChange={(e) => setSortOrder(e.target.value)}
                              style={{ width: 'auto' }}
                            >
                              <option value="desc">Descending</option>
                              <option value="asc">Ascending</option>
                            </select>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {loading ? (
                          <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                              <span className="visually-hidden">Loading events...</span>
                            </Spinner>
                            <p className="mt-2">Loading events...</p>
                          </div>
                        ) : sortedEventsList.length > 0 ? (
                          <div className="table-responsive">
                            <Table striped hover>
                              <thead>
                                <tr>
                                  <th>
                                    Event Title
                                    {sortBy === 'title' && (
                                      <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                    )}
                                  </th>
                                  <th>
                                    Date
                                    {sortBy === 'date' && (
                                      <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                    )}
                                  </th>
                                  <th>Status</th>
                                  <th>Location</th>
                                  <th>Price</th>
                                  <th>
                                    Orders
                                    {sortBy === 'orderCount' && (
                                      <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                    )}
                                  </th>
                                  <th>
                                    Revenue
                                    {sortBy === 'revenue' && (
                                      <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                    )}
                                  </th>
                                  <th>Organizer</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedEventsList.map((event) => (
                                  <tr key={event.id}>
                                    <td>
                                      <strong>{event.title}</strong>
                                    </td>
                                    <td>
                                      <div>
                                        <div>{moment(event.startDate.toDate()).format('MMM DD, YYYY')}</div>
                                        <small className="text-muted">
                                          {moment(event.startDate.toDate()).format('h:mm A')}
                                          {event.endDate && ` - ${moment(event.endDate.toDate()).format('h:mm A')}`}
                                        </small>
                                      </div>
                                    </td>
                                    <td>
                                      <Badge 
                                        bg={
                                          event.status === 'draft' ? 'secondary' :
                                          event.status === 'deleted' ? 'danger' :
                                          event.status === 'published' ? 'success' : 'warning'
                                        }
                                      >
                                        {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                                      </Badge>
                                    </td>
                                    <td>{event.location}</td>
                                    <td>
                                      {event.isFree ? (
                                        <Badge bg="secondary">FREE</Badge>
                                      ) : (
                                        <div>
                                          <Badge bg="warning">${event.maxPrice}</Badge>
                                          {Object.keys(event.prices).length > 1 && (
                                            <small className="text-muted d-block">
                                              {Object.keys(event.prices).length} price tiers
                                            </small>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <Badge bg="primary">{event.orderCount}</Badge>
                                    </td>
                                    <td>
                                      <Badge bg="success">${event.totalRevenue.toFixed(2)}</Badge>
                                    </td>
                                    <td>{event.organizer}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-muted">No events found for {getMonthName(selectedListMonth)} {currentYear}</p>
                          </div>
                        )}
                        
                        {/* Events Summary */}
                        {sortedEventsList.length > 0 && (
                          <div className="mt-3">
                            <Row>
                              <Col md={3}>
                                <div className="text-center">
                                  <h5 className="text-primary">{sortedEventsList.length}</h5>
                                  <small className="text-muted">Total Events</small>
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className="text-center">
                                  <h5 className="text-success">
                                    {sortedEventsList.reduce((sum, event) => sum + event.orderCount, 0)}
                                  </h5>
                                  <small className="text-muted">Total Orders</small>
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className="text-center">
                                  <h5 className="text-warning">
                                    ${sortedEventsList.reduce((sum, event) => sum + event.totalRevenue, 0).toFixed(2)}
                                  </h5>
                                  <small className="text-muted">Total Revenue</small>
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className="text-center">
                                  <h5 className="text-info">
                                    {sortedEventsList.length > 0 
                                      ? (sortedEventsList.reduce((sum, event) => sum + event.orderCount, 0) / sortedEventsList.length).toFixed(1)
                                      : 0
                                    }
                                  </h5>
                                  <small className="text-muted">Avg Orders/Event</small>
                                </div>
                              </Col>
                            </Row>
                            
                            {/* Event Status Summary */}
                            <hr className="my-3" />
                            <Row>
                              <Col md={12}>
                                <h6 className="mb-3">Event Status Breakdown</h6>
                                <Row>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h5 className="text-secondary">{eventStatusCounts.draft}</h5>
                                      <small className="text-muted">Draft Events</small>
                                    </div>
                                  </Col>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h5 className="text-danger">{eventStatusCounts.deleted}</h5>
                                      <small className="text-muted">Deleted Events</small>
                                    </div>
                                  </Col>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h5 className="text-success">{eventStatusCounts.published}</h5>
                                      <small className="text-muted">Published Events</small>
                                    </div>
                                  </Col>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h5 className="text-warning">{eventStatusCounts.other}</h5>
                                      <small className="text-muted">Other Status</small>
                                    </div>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Additional Analytics */}
              <Row className="mt-4">
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Event Distribution</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Free Events:</span>
                        <Badge bg="secondary">{(viewMode === 'monthly' ? getTotalForYear('freeEvents') : viewMode === 'daily' ? getTotalForMonth('freeEvents') : listData.freeEvents).toLocaleString()}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Paid Events:</span>
                        <Badge bg="warning">{(viewMode === 'monthly' ? getTotalForYear('paidEvents') : viewMode === 'daily' ? getTotalForMonth('paidEvents') : listData.paidEvents).toLocaleString()}</Badge>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Free vs Paid Ratio:</span>
                        <span>
                          {(viewMode === 'monthly' ? getTotalForYear('totalEvents') : viewMode === 'daily' ? getTotalForMonth('totalEvents') : listData.totalEvents) > 0 
                            ? `${(((viewMode === 'monthly' ? getTotalForYear('freeEvents') : viewMode === 'daily' ? getTotalForMonth('freeEvents') : listData.freeEvents) / (viewMode === 'monthly' ? getTotalForYear('totalEvents') : viewMode === 'daily' ? getTotalForMonth('totalEvents') : listData.totalEvents)) * 100).toFixed(1)}% free`
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Ticket Distribution</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Free Tickets:</span>
                        <Badge bg="light" text="dark">{(viewMode === 'monthly' ? getTotalForYear('freeTickets') : viewMode === 'daily' ? getTotalForMonth('freeTickets') : listData.freeTickets).toLocaleString()}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Paid Tickets:</span>
                        <Badge bg="danger">{(viewMode === 'monthly' ? getTotalForYear('paidTickets') : viewMode === 'daily' ? getTotalForMonth('paidTickets') : listData.paidTickets).toLocaleString()}</Badge>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Free vs Paid Ratio:</span>
                        <span>
                          {(viewMode === 'monthly' ? getTotalForYear('totalTicketSales') : viewMode === 'daily' ? getTotalForMonth('totalTicketSales') : listData.totalTicketSales) > 0 
                            ? `${(((viewMode === 'monthly' ? getTotalForYear('freeTickets') : viewMode === 'daily' ? getTotalForMonth('freeTickets') : listData.freeTickets) / (viewMode === 'monthly' ? getTotalForYear('totalTicketSales') : viewMode === 'daily' ? getTotalForMonth('totalTicketSales') : listData.totalTicketSales)) * 100).toFixed(1)}% free`
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminMonthlyAnalytics;
